import { z } from 'zod';
import { refineTableName } from '../Refines';

export const TableModes = ['configure', 'custom', 'disable'] as const;
export type TableMode = (typeof TableModes)[number];

export const EmbedColumnOptions = [
  { name: 'JSON', value: 'json' },
  { name: 'Struct', value: 'struct' },
  { name: 'Variant', value: 'variant' },
] as const;
export const EmbedColumnTypes = EmbedColumnOptions.map(
  (option) => option.value,
);
export type EmbedColumnType = (typeof EmbedColumnTypes)[number];

export const AssertSchema = z.object({
  expr: z.string().optional(),
  message: z.string().optional(),
});

export const FieldSpecJoinSchema = z.object({
  useCSV: z.boolean().optional(),
  withTable: z.string().optional(),
  withCSV: z
    .object({
      path: z.string().optional(),
    })
    .optional(),
  lhs: z.string().optional(),
  rhs: z.string().optional(),
  select: z.string().optional(),
});

export const FieldSpecSchema = z.object({
  name: z.string().optional(),
  from: z.string().optional(),
  alias: z.string().optional(),
  expr: z.string().optional(),
  literal: z.string().optional(),
  join: FieldSpecJoinSchema.optional(),
  comment: z.string().optional(),
  assert: z.array(AssertSchema).optional(),
});

export type FieldSpecSchemaValues = z.infer<typeof FieldSpecSchema>;

export const KeyValueSchema = z.object({
  key: z.string().optional(),
  value: z.string().optional(),
});

export const JSONExtractionSchema = z.object({
  source: z.string({
    required_error: 'A column is required',
  }),
  omitFields: z.array(z.string().optional()).optional(),
  duplicatePrefix: z.string().optional(),
  embed: z.boolean(),
  embedColumn: z.string().optional(),
});

export type JSONExtractionSchemaValues = z.infer<typeof JSONExtractionSchema>;

export const JSONExtractionsSchema = z.object({
  jsonExtract: z.array(JSONExtractionSchema),
});

export type JSONExtractionsSchemaValues = z.infer<typeof JSONExtractionsSchema>;

export const PreserveUnreferencedColumnsSchema = z.object({
  preserve: z.boolean(),
  embed: z.boolean(),
  embedColumn: z.string().optional(),
  embedColumnType: z.enum(['', ...EmbedColumnTypes]).optional(),
  duplicatePrefix: z.string().optional(),
  omitColumns: z.array(z.string().optional()).optional(),
});

export type PreserveUnreferencedColumnsSchemaValues = z.infer<
  typeof PreserveUnreferencedColumnsSchema
>;

export const CustomFunctionSchema = z.object({
  customFunction: z.string().optional(),
  customFunctionOptions: z.array(KeyValueSchema),
});

export type CustomFunctionSchemaValues = z.infer<typeof CustomFunctionSchema>;

export const FiltersSchema = z.object({
  filter: z.string().optional(),
  postFilter: z.string().optional(),
});

export type FiltersSchemaValues = z.infer<typeof FiltersSchema>;

export const OverrideLiquidColumnsSchema = z.object({
  enableOverrideLiquidColumns: z.boolean(),
  overrideLiquidColumns: z.array(z.string().optional()),
});

export type OverrideLiquidColumnsSchemaValues = z.infer<
  typeof OverrideLiquidColumnsSchema
>;

export const TemporaryFieldSchema = z.object({
  temporaryFields: z.array(FieldSpecSchema),
});

export type TemporaryFieldSchemaValues = z.infer<typeof TemporaryFieldSchema>;

export const TableModeSchema = z.object({
  tableMode: z.enum(TableModes),
});

export const DataSourceTableSchema = z
  .object({
    name: z
      .string({
        required_error: 'Name is required',
      })
      .refine(...refineTableName),
    sourceTable: z.string().optional(),
  })
  .merge(FiltersSchema)
  .merge(JSONExtractionsSchema)
  .merge(CustomFunctionSchema)
  .merge(PreserveUnreferencedColumnsSchema)
  .merge(OverrideLiquidColumnsSchema)
  .merge(TableModeSchema)
  .merge(TemporaryFieldSchema);

export type DataSourceTableSchemaValues = z.infer<typeof DataSourceTableSchema>;

export const AddTableSchema = z
  .object({
    fields: z.array(FieldSpecSchema),
  })
  .merge(DataSourceTableSchema);

export type AddTableSchemaValues = z.infer<typeof AddTableSchema>;

export const ModifyTableSchema = z
  .object({
    omitFields: z.array(z.string()),
    addFields: z.array(FieldSpecSchema),
  })
  .merge(DataSourceTableSchema);

export type ModifyTableSchemaValues = z.infer<typeof ModifyTableSchema>;

export const DataSourceTablesSchema = z.object({
  addTables: z.array(AddTableSchema),
  modifyTables: z.array(ModifyTableSchema),
  customNotebookPath: z.string().optional(),
});

export type DataSourceTablesSchemaValues = z.infer<
  typeof DataSourceTablesSchema
>;
